import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTheme } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
} from "@mui/x-data-grid-premium";
import dayjs from "dayjs";

import { StatusLabel } from "@/Components/v2/atoms/StatusLabel";
import { TypeLabel } from "@/Components/v2/atoms/TypeLabel";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { ABBREVIATED_OPTION, useConfig } from "@/hooks/useConfig";
import { Status } from "@/pages/scenario-builder/types";
import { formatPercentage } from "@/pages/scenario-builder/utils";
import {
  createDeleteOption,
  createShareLinkOption,
  TableContextMenu,
} from "@/pages/scenario-builder/v3/components/TableContextMenu";
import { formatDateLocale, getDate } from "@/shared/utils/formatDate";

import { ScenarioType, ScenarioTypeEnum, StatusEnum } from "../scenario-form/types";

export interface TableRow {
  id: string;
  scenarioId: string;
  nodeId: string;
  scenarioName: string;
  type: string;
  typeDisplayName?: string;
  createdBy: string;
  createdOn: Date;
  simulatedSpend: number;
  simulatedRevenue: number;
  simulatedROI: number;
  status: StatusEnum;
  statusMessage: string;
  targetNodeUUID: string;
  brand: string;
  startDate: Date;
  endDate: Date;
  scenarioCount: number;
}

interface UseScenariosPageColumnsOptions {
  onDelete: (scenarioId: string) => void;
  onDuplicate: (scenarioId: string) => void;
  isGeoEnabled: boolean;
}

export const useScenariosPageColumns = ({
  onDelete,
  onDuplicate,
  isGeoEnabled,
}: UseScenariosPageColumnsOptions): GridColDef[] => {
  const { config } = useConfig();
  const theme = useTheme();
  const formatCurrency = config.currency.formatCurrency;

  const { mapDefaultNodeName } = useDefaultNodeNameInfo();

  const baseColumns: GridColDef<TableRow>[] = [
    {
      field: "scenarioName",
      headerName: "Name",
      minWidth: 200,
      flex: 1,
      type: "string",
    },
    {
      field: "scope",
      headerName: "Scope",
      sortable: true,
      minWidth: 150,
      flex: 0.8,
      type: "string",
      valueGetter: ({ row }) => {
        return mapDefaultNodeName(row.brand) ?? "-";
      },
    },

    {
      field: "type",
      headerName: "Type",
      sortable: true,
      minWidth: 160,
      flex: 0.8,
      type: "string",
      renderCell: (params: GridRenderCellParams) => {
        const scenarioRow = params.row as TableRow;
        const scenarioType = (scenarioRow?.type as ScenarioTypeEnum) || ScenarioTypeEnum.Unknown;

        let style = ScenarioType.getStyle(scenarioType, theme, scenarioRow.scenarioCount);

        // Use typeDisplayName if available (from v2 tables), otherwise generate the label for V2 mode
        const label =
          scenarioRow.typeDisplayName ||
          ScenarioType.getDisplayName(scenarioType, scenarioRow.scenarioCount);

        // In V2 mode (always enabled), Super Optimizer should use optimizer color
        if (ScenarioType.isSuperOptimizer(scenarioType, scenarioRow.scenarioCount)) {
          style = {
            labelColor: theme.palette.scenarioType.optimizer,
          };
        }

        return <TypeLabel label={label} labelColor={style.labelColor} />;
      },
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 120,
      valueGetter: ({ row: { startDate } }) => getDate(startDate) ?? "-",
    },
    {
      field: "endDate",
      headerName: "End Date",
      width: 120,
      valueGetter: ({ row: { endDate } }) => getDate(endDate) ?? "-",
    },
    {
      field: "createdBy",
      headerName: getColumnHeaderName("createdBy"),
      minWidth: 150,
      flex: 0.8,
      type: "string",
    },
    {
      field: "createdOn",
      headerName: getColumnHeaderName("createdOn"),
      minWidth: 100,
      flex: 0.5,
      type: "date",
      valueFormatter: (params: GridValueFormatterParams) => {
        return formatDateLocale(dayjs(params.value));
      },
    },
    {
      field: "simulatedSpend",
      headerName: "Spend",
      description: "Simulated Spend",
      sortable: true,
      minWidth: 100,
      flex: 0.5,
      type: "number",
      headerAlign: "left",
      align: "left",
      valueFormatter: (params: GridValueFormatterParams) => {
        if (params.value == null) return "-";
        return formatCurrency(params.value, ABBREVIATED_OPTION);
      },
    },
    {
      field: "simulatedRevenue",
      headerName: "Revenue",
      description: "Simulated Revenue",
      sortable: true,
      minWidth: 100,
      flex: 0.5,
      type: "number",
      headerAlign: "left",
      align: "left",
      valueFormatter: (params: GridValueFormatterParams) => {
        if (params.value == null) return "-";
        return formatCurrency(params.value, ABBREVIATED_OPTION);
      },
    },
    {
      field: "simulatedROI",
      headerName: "ROI",
      description: "Simulated ROI",
      sortable: true,
      minWidth: 80,
      flex: 0.4,
      type: "number",
      headerAlign: "left",
      align: "left",
      valueFormatter: (params: GridValueFormatterParams) => {
        if (params.value == null) return "-";
        return formatPercentage(params.value);
      },
    },
    {
      field: "status",
      headerName: getColumnHeaderName("status"),
      minWidth: 140,
      flex: 0.6,
      type: "string",
      renderCell: ({ value }: GridRenderCellParams<any, string>) => {
        const label = Status.toString(value as StatusEnum, true);
        const style = Status.getStyle(value as StatusEnum, theme);
        return <StatusLabel label={label} style={style} />;
      },
    },
    ...(isGeoEnabled
      ? [
          {
            field: "geography",
            headerName: "Geography",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params: GridValueFormatterParams) => {
              if (!params.value) return "-";
              return params.value;
            },
          },
        ]
      : []),
    {
      field: "contextMenu",
      headerName: "",
      width: 50,
      resizable: false,
      sortable: false,
      type: "actions",
      renderCell: (params: GridRenderCellParams) => {
        const { id, status } = params.row;
        const basePath = `/scenarios-v3/${id}`;
        const shareUrl = status === StatusEnum.Draft ? `${basePath}/form` : `${basePath}/results`;

        return (
          <TableContextMenu
            options={[
              createShareLinkOption(shareUrl, "Scenario URL copied to clipboard"),
              { label: "Duplicate", icon: ContentCopyIcon, onClick: () => onDuplicate(id) },
              createDeleteOption(() => onDelete(id)),
            ]}
          />
        );
      },
    },
  ];

  return baseColumns;
};

export type ColumnField =
  | "scenarioName"
  | "scope"
  | "type"
  | "startDate"
  | "endDate"
  | "createdBy"
  | "createdOn"
  | "simulatedSpend"
  | "simulatedRevenue"
  | "simulatedROI"
  | "status"
  | "geography"
  | "contextMenu";

export const getAllColumnFields = (): ColumnField[] => {
  return [
    "scenarioName",
    "scope",
    "type",
    "startDate",
    "endDate",
    "createdBy",
    "createdOn",
    "simulatedSpend",
    "simulatedRevenue",
    "simulatedROI",
    "status",
    "geography",
  ];
};

export const getColumnHeaderName = (field: ColumnField): string => {
  // Define a static mapping of field names to header names
  const headerNames: Record<ColumnField, string> = {
    scenarioName: "Name",
    scope: "Scope",
    type: "Type",
    startDate: "Start Date",
    endDate: "End Date",
    createdBy: "Created By",
    createdOn: "Created On",
    simulatedSpend: "Simulated Spend",
    simulatedRevenue: "Simulated Revenue",
    simulatedROI: "Simulated ROI",
    status: "Status",
    geography: "Geography",
    contextMenu: "",
  };

  return headerNames[field];
};
